import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { format, startOfDay, subDays } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useObjetivosPichincha } from '../hooks/useObjetivosPichincha';
import { API_DISPONIBILIDAD_URL } from '../../../common/globals/constants';

const TODOS_OBJETIVOS_VALUE = 'todos';

export function SincronizacionView() {
  const { t } = useTranslation();
  const { data: objectivesToDisplay, isLoading: isLoadingObjectiveList } =
    useObjetivosPichincha();

  const DISPONIBILIDAD_OPTIONS = [
    { value: '3', label: t('sync.availability.sync.label.pichinchaDisp') },
    { value: '2', label: t('sync.availability.sync.label.normalDisp') },
  ];

  const [selectedDate, setSelectedDate] = React.useState(
    startOfDay(subDays(new Date(), 1)),
  );
  const [selectedObjetivoIds, setSelectedObjetivoIds] = React.useState([]);
  const [tipoDisponibilidad, setTipoDisponibilidad] =
    React.useState('');
  const [isSending, setIsSending] = React.useState(false);

  const allSelected =
    objectivesToDisplay.length > 0 &&
    selectedObjetivoIds.length === objectivesToDisplay.length;

  function handleChangeObjective(event) {
    const value = event.target.value;

    // Si el usuario acaba de hacer clic en "Todos"
    if (value.includes(TODOS_OBJETIVOS_VALUE)) {
      if (allSelected) {
        // Si ya estaban todos, deseleccionar todos
        setSelectedObjetivoIds([]);
      } else {
        // Seleccionar todos
        setSelectedObjetivoIds(
          objectivesToDisplay.map((o) => o.objetivo_id.toString()),
        );
      }
      return;
    }

    setSelectedObjetivoIds(value);
  }

  function handleChangeTipoDisponibilidad(event) {
    setTipoDisponibilidad(event.target.value);
  }

  function handleChangeDate(date) {
    setSelectedDate(date);
  }

  function getSelectedObjetivos() {
    return objectivesToDisplay.filter((item) =>
      selectedObjetivoIds.includes(item.objetivo_id.toString()),
    );
  }

  async function handleEnviar() {
    if (selectedObjetivoIds.length === 0) {
      toast.warning(t('sync.availability.sync.toast.selectObjective'));
      return;
    }
    if (!tipoDisponibilidad) {
      toast.warning(t('sync.availability.sync.toast.selectDispType'));
      return;
    }

    const objetivos = getSelectedObjetivos();
    if (objetivos.length === 0) {
      toast.error(t('sync.availability.sync.toast.noObjectivesFound'));
      return;
    }

    setIsSending(true);

    try {
      const results = await Promise.allSettled(
        objetivos.map((objetivo) => {
          const url = `${API_DISPONIBILIDAD_URL}/enviar/${objetivo.objetivo_id}/${tipoDisponibilidad}/${selectedDate}`;
          return fetch(url, { method: 'POST' });
        }),
      );

      const exitosos = results.filter((r) => r.status === 'fulfilled').length;
      const fallidos = results.filter((r) => r.status === 'rejected').length;

      if (fallidos === 0) {
        toast.success(
          t('sync.availability.sync.toast.sendSuccess', {
            count: exitosos,
            plural: exitosos > 1 ? 's' : '',
          }),
        );
      } else {
        toast.warning(
          t('sync.availability.sync.toast.sendPartial', {
            exitosos,
            fallidos,
          }),
        );
      }

      results.forEach((result, index) => {
        const obj = objetivos[index];
        if (result.status === 'rejected') {
          console.error(
            `Error enviando objetivo ${obj.objetivo_id} (${obj.nombre}):`,
            result.reason,
          );
        } else {
          console.log(
            `OK: objetivo ${obj.objetivo_id} (${obj.nombre}) - Status: ${result.value.status}`,
          );
        }
      });
    } catch (e) {
      console.error(e);
      toast.error(t('sync.availability.sync.toast.unexpectedError'));
    } finally {
      setIsSending(false);
    }
  }

  return (
    <Box
      marginTop={4}
      display="flex"
      justifyContent="center"
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 500,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="h6" fontWeight="bold" textAlign="center">
          {t('sync.availability.sync.configTitle')}
        </Typography>

        <Box>
          <TextField type='date' onChange={(e) => handleChangeDate(e.target.value)} value={selectedDate} size='small' label={t('sync.availability.sync.label.date')} fullWidth inputProps={{
            max: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            min: format(subDays(new Date(), 7), 'yyyy-MM-dd')
          }} />
        </Box>

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel id="select-objetivo-disp">{t('sync.availability.sync.label.objectives')}</InputLabel>
          <Select
            labelId="select-objetivo-disp"
            multiple
            value={selectedObjetivoIds}
            onChange={handleChangeObjective}
            disabled={isLoadingObjectiveList}
            label={t('sync.availability.sync.label.objectives')}
            renderValue={(selected) => {
              if (selected.length === objectivesToDisplay.length) {
                return <Chip label={t('sync.availability.sync.label.allObjectives')} size="small" />;
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const obj = objectivesToDisplay.find(
                      (o) => o.objetivo_id.toString() === id,
                    );
                    return (
                      <Chip
                        key={id}
                        label={obj?.nombre ?? id}
                        size="small"
                      />
                    );
                  })}
                </Box>
              );
            }}
          >
            <MenuItem value={TODOS_OBJETIVOS_VALUE}>
              <Checkbox checked={allSelected} />
              <ListItemText primary={<strong>{t('sync.availability.sync.label.allObjectives')}</strong>} />
            </MenuItem>
            {objectivesToDisplay.map(({ objetivo_id, nombre }) => (
              <MenuItem key={objetivo_id} value={objetivo_id.toString()}>
                <Checkbox
                  checked={selectedObjetivoIds.includes(
                    objetivo_id.toString(),
                  )}
                />
                <ListItemText primary={nombre} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel id="select-tipo-disponibilidad">
            {t('sync.availability.sync.label.dispType')}
          </InputLabel>
          <Select
            labelId="select-tipo-disponibilidad"
            value={tipoDisponibilidad}
            onChange={handleChangeTipoDisponibilidad}
            label={t('sync.availability.sync.label.dispType')}
          >
            <MenuItem aria-label="" value="">
              <em>{t('sync.availability.sync.label.selectType')}</em>
            </MenuItem>
            {DISPONIBILIDAD_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {value} - {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleEnviar}
          endIcon={<SendIcon />}
          fullWidth
          disabled={isSending}
          style={{ backgroundColor: "#FE7100", color: "white" }}
        >
          {isSending ? t('sync.availability.sync.status.sending') : t('sync.availability.sync.status.send')}
        </Button>
      </Box>
    </Box>
  );
}
